import { Trans, useLingui } from "@lingui/react/macro";
import { formatDate } from "@offstreetparking/design-system/formatters";
import { Button } from "@offstreetparking/design-system/forms";
import { useLocale } from "@offstreetparking/design-system/providers";
import { Link, Text } from "@offstreetparking/design-system/typography";
import { routes } from "../../../../routes";
import { FlowModal as Modal } from "../../../overlays/FlowModal/FlowModal";
import { FlowModalTitle as ModalTitle } from "../../../overlays/FlowModal/FlowModalTitle";
import type { PermitFormData } from "../../PermitContextProvider";
export type ExistingPermitOrRequest =
	| { kind: "permit"; publicIdentifier: string }
	| { kind: "request"; requestId: string };
type ExistingPermitModalProps = {
	plate: string;
	permitIntervals: PermitFormData["permitIntervals"];
	timezone: string;
	existing: ExistingPermitOrRequest;
	onClose: () => void;
	locationContact?: {
		email?: string | undefined;
		formattedPhoneNumber?: string | undefined;
	};
};
export const ExistingPermitModal = ({
	plate,
	permitIntervals,
	timezone,
	existing,
	onClose,
	locationContact,
}: ExistingPermitModalProps) => {
	const { t } = useLingui();
	const { locale } = useLocale();
	return (
		<Modal
			i18n={{
				closeButton: t`Close`,
				cancelButton: t`Cancel`,
			}}
			isOpen={true}
			onClose={onClose}
			actions={
				<>
					{existing.kind === "permit" ? (
						<Button
							intent="primary"
							href={routes.permitConfirmation({ publicIdentifier: existing.publicIdentifier })}
						>
							{t`View Permit`}
						</Button>
					) : (
						<Button intent="primary" href={routes.permitRequest({ publicIdentifier: existing.requestId })}>
							{t`View Request`}
						</Button>
					)}
					<Button intent="secondary" type="button" onClick={onClose}>
						{t`Cancel`}
					</Button>
				</>
			}
		>
			<ModalTitle>
				{existing.kind === "permit" ? t`You're already registered!` : t`You've already submitted a request!`}
			</ModalTitle>

			<div className="space-y-4">
				{(permitIntervals ?? []).map((interval) => {
					const from = formatDate({
						date: interval.startDateTimeISO,
						formatType: "Abbreviated_DateTime",
						timezone: timezone,
						language: locale,
					});
					const to = interval.endDateTimeISO
						? formatDate({
								date: interval.endDateTimeISO,
								formatType: "Abbreviated_DateTime",
								timezone: timezone,
								language: locale,
							})
						: "";
					return (
						<Text key={interval.startDateTimeISO}>
							{existing.kind === "permit" ? (
								<Trans>
									<b>{plate}</b> is already registered from <b>{from}</b> to <b>{to}</b>
								</Trans>
							) : (
								<Trans>
									<b>{plate}</b> already has a request under review from <b>{from}</b> to <b>{to}</b>
								</Trans>
							)}
						</Text>
					);
				})}
				<Text>
					{locationContact?.email && locationContact?.formattedPhoneNumber ? (
						<Trans comment="Parking session — one vehicle's stay, looked up by confirmation number. Not a login session.">
							<span>To change session details, contact </span>
							<Link href={`mailto:${locationContact.email}`}>{locationContact.email}</Link>
							<span> or </span>
							<Link href={`tel:${locationContact.formattedPhoneNumber}`}>
								{locationContact.formattedPhoneNumber}
							</Link>
						</Trans>
					) : locationContact?.email ? (
						<Trans comment="Parking session — one vehicle's stay, looked up by confirmation number. Not a login session.">
							<span>To change session details, contact </span>
							<Link href={`mailto:${locationContact.email}`}>{locationContact.email}</Link>
						</Trans>
					) : locationContact?.formattedPhoneNumber ? (
						<Trans comment="Parking session — one vehicle's stay, looked up by confirmation number. Not a login session.">
							<span>To change session details, contact </span>
							<Link href={`tel:${locationContact.formattedPhoneNumber}`}>
								{locationContact.formattedPhoneNumber}
							</Link>
						</Trans>
					) : (
						t({
							message: "To change session details, contact your administrator",
							comment:
								"Parking session — one vehicle's stay, looked up by confirmation number. Not a login session.",
						})
					)}
				</Text>
			</div>
		</Modal>
	);
};
